import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu';
import ApiService from '../services/api';
import './Dashboard.css';

const Dashboard = ({ user, onSignOut }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000); // Auto-hide after 5 seconds
  };

  const hideNotification = () => {
    setNotification(null);
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getAllApplications();
      setApplications(data);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getSortedApplications = () => {
    return [...applications].sort((a, b) => {
      const dateA = new Date(a.generatedTimestamp || 0);
      const dateB = new Date(b.generatedTimestamp || 0);
      return dateB - dateA; // Always descending (newest first)
    });
  };

  const handleDownloadPitchDeck = async (app, e) => {
    e.stopPropagation(); // Prevent row click
    
    // Handle legacy powerpointUrl (direct link)
    if (!app.powerpointS3Key && app.powerpointUrl) {
      window.open(app.powerpointUrl, '_blank');
      return;
    }
    
    if (!app.powerpointS3Key) return;
    
    try {
      const downloadUrl = ApiService.generateSignedDownloadUrl(app.powerpointS3Key);
      const headers = await ApiService.getAuthHeaders();
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: headers
      });

      if (response.ok) {
        // Parse the JSON response to get the download URL
        const data = await response.json();
        if (data.downloadUrl) {
          // Fetch the actual file from the presigned URL
          const fileResponse = await fetch(data.downloadUrl);
          if (fileResponse.ok) {
            const blob = await fileResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = data.filename || app.pitchDeckName || 'pitch-deck.pdf';
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          } else {
            showNotification('Failed to download file from S3', 'error');
          }
        } else {
          showNotification('Failed to get download URL', 'error');
        }
      } else {
        showNotification('Failed to download pitch deck', 'error');
      }
    } catch (error) {
      console.error('Error downloading pitch deck:', error);
      showNotification('Error downloading pitch deck', 'error');
    }
  };

  const handleRowClick = (applicationId) => {
    navigate(`/application/${applicationId}`);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'approved': return '#86efac';
      case 'accepted': return '#22c55e'; // Bright green for accepted
      case 'rejected': return '#9ca3af'; // Blackish gray pastel
      case 'rejected after interview': return '#fca5a5'; // Previous rejected color (light red/pink)
      case 'interview scheduled': return '#93c5fd';
      case 'pending interview': return '#fed7aa';
      case 'under review': return '#c4b5fd';
      case 'invited': return '#a7f3d0'; // New status from POC
      case 'denied': return '#fca5a5'; // New status from POC
      case 'pending': return '#fde68a'; // New status from POC
      default: return '#d1d5db';
    }
  };

  const getDecisionClass = (decision) => {
    if (!decision || decision === 'N/A') return '';
    
    // Handle both capitalized and lowercase versions for backward compatibility
    const normalizedDecision = decision.toLowerCase();
    
    const lowInterestDecisions = ['join mailing list'];
    const mediumInterestDecisions = ['station membership'];
    const highInterestDecisions = ['startup in residence', 'close orbit'];
    
    if (lowInterestDecisions.includes(normalizedDecision)) return 'decision-low';
    if (mediumInterestDecisions.includes(normalizedDecision)) return 'decision-medium';
    if (highInterestDecisions.includes(normalizedDecision)) return 'decision-high';
    
    return '';
  };

  return (
    <div className="dashboard-container">
      {notification && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button className="notification-close" onClick={hideNotification}>
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <div className="applications-panel">
        <div className="panel-header">
          <h1 className="panel-title">Aethos Applications</h1>
          <UserMenu user={user} onSignOut={onSignOut} />
        </div>

        {!loading && (
          <div className="modern-table">
            {error ? (
              <div className="error-state">
                <p>Error: {error}</p>
                <button onClick={fetchApplications} className="retry-btn">
                  Retry
                </button>
              </div>
            ) : applications.length === 0 ? (
              <div className="empty-state">
                <p>No applications found.</p>
              </div>
            ) : (
              <>
                <div className="application-headers">
                  <div className="application-header-item name-header">Name</div>
                  <div className="application-header-item decision-header">Decision</div>
                  <div className="application-header-item status-header">Status</div>
                  <div className="application-header-item interviewer-header">Interviewer</div>
                  <div className="application-header-item date-header">Date Applied</div>
                  <div className="application-header-item download-header">Pitch Deck</div>
                </div>
                
                <div className="table-content">
                  {getSortedApplications().map((app) => (
                    <div 
                      key={app.id || app.email} 
                      className="application-row" 
                      onClick={() => handleRowClick(app.id || app.email)}
                    >
                      <div className="row-item name-item">
                        <span className="applicant-name">
                          {app.firstName} {app.lastName}
                        </span>
                      </div>
                      
                      <div className="row-item decision-item">
                        <div className={`decision-badge ${getDecisionClass(app.evaluationResult?.decision || app.decision || app.aiAnalysis?.finalDecision || app.grading?.finalDecision || app.grading?.decision)}`}>
                          {app.evaluationResult?.decision || app.decision || app.aiAnalysis?.finalDecision || app.grading?.finalDecision || app.grading?.decision || 'N/A'}
                        </div>
                      </div>
                      
                      <div className="row-item status-item">
                        <div 
                          className="status-badge" 
                          style={{ backgroundColor: getStatusColor(app.status) }}
                        >
                          {app.status}
                        </div>
                      </div>
                      
                      <div className="row-item interviewer-item">
                        {(() => {
                          const allAssigned = [...(app.assignedInterviewers || [])];
                          if (app.assignedInterviewer && !allAssigned.includes(app.assignedInterviewer)) {
                            allAssigned.push(app.assignedInterviewer);
                          }
                          
                          if (allAssigned.length > 1) {
                            return (
                              <div className="interviewer-badge multiple">
                                Multiple ({allAssigned.length})
                              </div>
                            );
                          } else if (allAssigned.length === 1) {
                            return (
                              <div className="interviewer-badge">
                                {allAssigned[0]}
                              </div>
                            );
                          } else {
                            return (
                              <div className="interviewer-badge unassigned">Not assigned</div>
                            );
                          }
                        })()}
                      </div>
                      
                      <div className="row-item date-item">
                        <span className="date-text">
                          {formatDate(app.generatedTimestamp)}
                        </span>
                      </div>
                      
                      <div className="row-item download-item">
                        <button 
                          onClick={(e) => handleDownloadPitchDeck(app, e)}
                          className="download-button"
                          disabled={!app.powerpointS3Key && !app.powerpointUrl}
                        >
                          <svg className="download-icon" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
